import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { AlertCircle, CheckCircle2, Download, FileSpreadsheet, LoaderCircle, RefreshCw, Trash2, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { getAuthHeaders } from '../contexts/AuthContext';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import { ConfirmDialog } from './ConfirmDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const PLATFORM_LABELS = {
  AMAZON_VENDOR: 'Amazon Vendor', AMAZON_SELLER: 'Amazon Seller', FLIPKART: 'Flipkart',
  FIRSTCRY: 'FirstCry', MYNTRA: 'Myntra', NYKAA: 'Nykaa', D2C_DROGO: 'D2C Drogo',
  D2C_BAYBEE: 'D2C Baybee', D2C_DOMESTICA: 'D2C Domestica', OTHERS: 'Other',
};

const escapeCsv = (value) => `"${String(value ?? '').replaceAll('"', '""')}"`;

const ROW_TONE = { error: 'border-red-200 bg-red-50/60', needs_validation: 'border-amber-200 bg-amber-50/60' };

export const BulkIdentifierImportDialog = ({ open, onOpenChange, country, onImported }) => {
  const inputRef = useRef(null);
  const [options, setOptions] = useState([]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [fileName, setFileName] = useState('');
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  // Work the user would lose by closing: rows they edited/removed, or rows left after a partial import.
  const [editedIds, setEditedIds] = useState(() => new Set());
  const [removedCount, setRemovedCount] = useState(0);
  const [partiallyImported, setPartiallyImported] = useState(false);
  // Discard prompt is captured when it opens ({ action: 'close' | 'replace', title, description })
  // so its text stays stable while the dialog animates out after reset().
  const [discardOpen, setDiscardOpen] = useState(false);
  const [discardPrompt, setDiscardPrompt] = useState(null);

  const validRows = useMemo(() => rows.filter(row => row.status !== 'error' && row.status !== 'needs_validation'), [rows]);
  const errorRows = useMemo(() => rows.filter(row => row.status === 'error'), [rows]);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/skus/identifier-config/options`, { headers: getAuthHeaders() })
      .then(response => setOptions(response.data.platforms || []))
      .catch(() => toast.error('Failed to load identifier options'));
  }, [open]);

  const reset = () => {
    setRows([]);
    setSummary(null);
    setFileName('');
    setDirty(false);
    setConfirmOpen(false);
    setEditedIds(new Set());
    setRemovedCount(0);
    setPartiallyImported(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  // Edited rows still in the preview plus rows the user removed.
  const editedCount = rows.filter(row => editedIds.has(row.row_id)).length + removedCount;
  const hasUnsavedWork = rows.length > 0 && (editedCount > 0 || partiallyImported);

  const askDiscard = (action) => {
    const plural = (n) => (n === 1 ? '' : 's');
    setDiscardPrompt({
      action,
      title: partiallyImported && !editedCount
        ? `Discard ${rows.length} remaining row${plural(rows.length)}?`
        : `Discard ${editedCount} edited row${plural(editedCount)}?`,
      description: `${action === 'replace' ? 'Replacing the file' : 'Closing'} throws away your corrections${partiallyImported ? ' and the rows that were not imported' : ''}. Rows already imported stay saved.`,
    });
    setDiscardOpen(true);
  };

  const handleOpenChange = (nextOpen) => {
    if (!nextOpen) {
      if (importing) return;
      if (hasUnsavedWork) { askDiscard('close'); return; }
      reset();
    }
    onOpenChange(nextOpen);
  };

  const replaceFile = () => {
    if (hasUnsavedWork) askDiscard('replace');
    else reset();
  };

  const confirmDiscard = () => {
    const action = discardPrompt?.action;
    reset();
    if (action === 'close') onOpenChange(false);
  };

  const markEdited = (rowId) => setEditedIds(current => (current.has(rowId) ? current : new Set(current).add(rowId)));

  const processFile = async (file) => {
    if (!file) return;
    const extension = file.name.toLowerCase().split('.').pop();
    if (!['csv', 'xls', 'xlsx'].includes(extension)) {
      toast.error('Upload a CSV, XLS, or XLSX file');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error('File must be 10 MB or smaller');
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(
        `${API}/skus/bulk-identifiers/preview-file?country=${country}`,
        formData,
        { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } }
      );
      setRows(response.data.rows || []);
      setSummary(response.data.summary || null);
      setFileName(file.name);
      setDirty(false);
      setEditedIds(new Set());
      setRemovedCount(0);
      setPartiallyImported(false);
      toast.success('Preview ready. Review every row before importing.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not preview identifier file');
    } finally {
      setLoading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const updateRow = (rowId, field, value) => {
    setRows(current => current.map(row => row.row_id === rowId ? {
      ...row,
      [field]: value,
      status: 'needs_validation',
      errors: [],
      warnings: [],
    } : row));
    markEdited(rowId);
    setSummary(null);
    setDirty(true);
  };

  const removeRow = (rowId) => {
    setRows(current => current.filter(row => row.row_id !== rowId));
    setEditedIds(current => {
      if (!current.has(rowId)) return current;
      const next = new Set(current);
      next.delete(rowId);
      return next;
    });
    setRemovedCount(count => count + 1);
    setSummary(null);
    setDirty(true);
  };

  const revalidate = async (sourceRows = rows) => {
    if (!sourceRows.length) return;
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/skus/bulk-identifiers/validate`,
        { country, rows: sourceRows },
        { headers: getAuthHeaders() }
      );
      setRows(response.data.rows || []);
      setSummary(response.data.summary || null);
      setDirty(false);
      toast.success('Corrections revalidated');
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not revalidate rows');
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/skus/bulk-identifiers/template`, {
        headers: getAuthHeaders(), responseType: 'blob',
      });
      const url = window.URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'platform_identifier_template.csv';
      anchor.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download template');
    }
  };

  const downloadErrors = () => {
    const header = ['master_sku', 'platform', 'identifier_type', 'identifier_value', 'label', 'is_primary', 'errors'];
    const lines = errorRows.map(row => [
      row.master_sku, row.platform, row.identifier_type, row.identifier_value,
      row.label, row.is_primary, (row.errors || []).join('; '),
    ].map(escapeCsv).join(','));
    const blob = new Blob([[header.join(','), ...lines].join('\n')], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'platform_identifier_errors.csv';
    anchor.click();
    window.URL.revokeObjectURL(url);
  };

  const confirmImport = async () => {
    setImporting(true);
    try {
      const response = await axios.post(
        `${API}/skus/bulk-identifiers/confirm`,
        { country, rows },
        { headers: getAuthHeaders() }
      );
      const imported = response.data.imported || 0;
      toast.success(response.data.message);
      setConfirmOpen(false);
      onImported?.();
      const unresolved = response.data.remaining_rows || [];
      const importErrors = response.data.import_errors || [];
      if (unresolved.length || importErrors.length) {
        const failedIds = new Map(importErrors.map(error => [error.row_id, error.reason]));
        const failedRows = rows
          .filter(row => failedIds.has(row.row_id))
          .map(row => ({ ...row, status: 'error', errors: [failedIds.get(row.row_id)] }));
        setPartiallyImported(true);
        // Submitted corrections are now either saved or in the remaining rows; partiallyImported covers them.
        setEditedIds(new Set());
        setRemovedCount(0);
        await revalidate([...unresolved, ...failedRows]);
        setFileName(`${fileName} · ${imported} imported`);
      } else {
        reset();
        onOpenChange(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Identifier import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="flex max-h-[92vh] max-w-[96vw] flex-col gap-0 overflow-hidden p-0 xl:max-w-7xl" data-testid="bulk-identifier-import-dialog">
          <DialogHeader className="border-b border-zinc-200 px-6 py-5 pr-12">
            <DialogTitle className="flex items-center gap-2 text-lg"><FileSpreadsheet className="h-5 w-5 text-blue-600" />Bulk import identifiers</DialogTitle>
            <DialogDescription>Upload, validate, correct, and confirm marketplace mappings before anything is saved.</DialogDescription>
          </DialogHeader>

          {!rows.length ? (
            <div className="overflow-y-auto p-6">
              <div
                className={`flex min-h-64 cursor-pointer flex-col items-center justify-center border-2 border-dashed p-8 text-center transition-colors duration-200 ${dragging ? 'border-blue-500 bg-blue-50' : 'border-zinc-300 bg-zinc-50 hover:border-blue-400 hover:bg-blue-50/40'}`}
                onClick={() => inputRef.current?.click()}
                onDragOver={event => { event.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={event => { event.preventDefault(); setDragging(false); processFile(event.dataTransfer.files?.[0]); }}
                role="button"
                tabIndex={0}
                onKeyDown={event => (event.key === 'Enter' || event.key === ' ') && inputRef.current?.click()}
                data-testid="identifier-upload-dropzone"
              >
                {loading ? <LoaderCircle className="mb-4 h-8 w-8 animate-spin text-blue-600" /> : <Upload className="mb-4 h-8 w-8 text-blue-600" />}
                <p className="font-semibold text-zinc-900">{loading ? 'Validating file…' : 'Drop CSV or Excel here'}</p>
                <p className="mt-1 text-sm text-zinc-500">or click to browse · CSV, XLS, XLSX · max 10 MB</p>
                <Input ref={inputRef} type="file" accept=".csv,.xls,.xlsx" className="hidden" onChange={event => processFile(event.target.files?.[0])} data-testid="identifier-file-input" />
              </div>
              <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-zinc-500">Required: master_sku, platform, identifier_type, identifier_value</p>
                <Button variant="outline" onClick={downloadTemplate} data-testid="download-identifier-template-button"><Download className="mr-2 h-4 w-4" />Download template</Button>
              </div>
            </div>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="flex flex-wrap items-center gap-3 border-b border-zinc-200 bg-zinc-50 px-5 py-3">
                <span className="max-w-xs truncate text-sm font-medium text-zinc-800" data-testid="identifier-import-filename">{fileName}</span>
                <Badge variant="outline" data-testid="identifier-import-total">{rows.length} rows</Badge>
                <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50" data-testid="identifier-import-valid">{validRows.length} valid</Badge>
                {summary?.create_rows > 0 && <Badge variant="outline" data-testid="identifier-import-create-count">{summary.create_rows} create</Badge>}
                {summary?.update_rows > 0 && <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700" data-testid="identifier-import-update-count">{summary.update_rows} update</Badge>}
                {errorRows.length > 0 && <Badge className="bg-red-50 text-red-700 hover:bg-red-50" data-testid="identifier-import-invalid">{errorRows.length} blocked</Badge>}
                {dirty && <Badge className="bg-amber-50 text-amber-700 hover:bg-amber-50" data-testid="identifier-import-dirty">Corrections need validation</Badge>}
                <div className="ml-auto flex flex-wrap gap-2">
                  {errorRows.length > 0 && <Button variant="outline" size="sm" onClick={downloadErrors} data-testid="download-identifier-errors-button"><Download className="mr-2 h-4 w-4" />Errors CSV</Button>}
                  <Button variant="outline" size="sm" onClick={replaceFile} disabled={loading || importing} data-testid="replace-identifier-file-button"><X className="mr-2 h-4 w-4" />Replace file</Button>
                </div>
              </div>

              <div className="min-h-0 flex-1 overflow-auto" data-testid="identifier-preview-grid">
                <div className="min-w-[1260px]">
                  <div className="grid grid-cols-[52px_150px_150px_150px_190px_150px_90px_minmax(220px,1fr)_44px] border-b border-zinc-200 bg-zinc-100 px-3 py-2 text-[11px] font-semibold uppercase text-zinc-500">
                    <span>Row</span><span>Master SKU</span><span>Platform</span><span>Type</span><span>Identifier</span><span>Label</span><span>Primary</span><span>Status</span><span />
                  </div>
                  {rows.map((row, index) => {
                    const types = options.find(option => option.value === row.platform)?.identifier_types || [];
                    return (
                      <div key={row.row_id} className={`grid grid-cols-[52px_150px_150px_150px_190px_150px_90px_minmax(220px,1fr)_44px] items-start gap-0 border-b px-3 py-3 ${ROW_TONE[row.status] || 'border-zinc-200 bg-white'}`} data-testid={`identifier-preview-row-${index}`}>
                        <span className="pt-2 text-xs text-zinc-500">{row.source_row}</span>
                        <Input value={row.master_sku} onChange={event => updateRow(row.row_id, 'master_sku', event.target.value)} className="h-8 rounded-none border-r-0 font-mono text-xs" data-testid={`identifier-row-${index}-master-sku-input`} />
                        <Select value={row.platform} onValueChange={value => { updateRow(row.row_id, 'platform', value); updateRow(row.row_id, 'identifier_type', ''); }}>
                          <SelectTrigger className="h-8 rounded-none border-r-0 text-xs" data-testid={`identifier-row-${index}-platform-select`}><SelectValue placeholder="Platform" /></SelectTrigger>
                          <SelectContent>{options.map(option => <SelectItem key={option.value} value={option.value} data-testid={`identifier-row-${index}-platform-option-${option.value.toLowerCase()}`}>{PLATFORM_LABELS[option.value] || option.value}</SelectItem>)}</SelectContent>
                        </Select>
                        <Select value={row.identifier_type} onValueChange={value => updateRow(row.row_id, 'identifier_type', value)}>
                          <SelectTrigger className="h-8 rounded-none border-r-0 text-xs" data-testid={`identifier-row-${index}-type-select`}><SelectValue placeholder="Type" /></SelectTrigger>
                          <SelectContent>{types.map(type => <SelectItem key={type} value={type} data-testid={`identifier-row-${index}-type-option-${type.toLowerCase()}`}>{type.replaceAll('_', ' ')}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input value={row.identifier_value} onChange={event => updateRow(row.row_id, 'identifier_value', event.target.value)} className="h-8 rounded-none border-r-0 font-mono text-xs" data-testid={`identifier-row-${index}-value-input`} />
                        <Input value={row.label || ''} onChange={event => updateRow(row.row_id, 'label', event.target.value)} className="h-8 rounded-none border-r-0 text-xs" data-testid={`identifier-row-${index}-label-input`} />
                        <div className="flex h-8 items-center justify-center border border-r-0 border-zinc-200 bg-white">
                          <Switch checked={Boolean(row.is_primary)} onCheckedChange={value => updateRow(row.row_id, 'is_primary', value)} aria-label={`Primary row ${row.source_row}`} data-testid={`identifier-row-${index}-primary-switch`} />
                        </div>
                        <div className="px-3 pt-1 text-xs" data-testid={`identifier-row-${index}-status`}>
                          {row.status === 'error' && <><span className="font-semibold text-red-700">Blocked</span><p className="mt-1 text-red-600">{(row.errors || []).join(' · ')}</p></>}{row.status === 'needs_validation' && <span className="font-semibold text-amber-700">Needs validation</span>}{!ROW_TONE[row.status] && <><span className="font-semibold text-emerald-700">{row.action === 'update' ? 'Will update' : 'Will create'}</span>{row.warnings?.length > 0 && <p className="mt-1 text-amber-700">{row.warnings.join(' · ')}</p>}</>}
                        </div>
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-zinc-500 hover:bg-red-50 hover:text-red-600" onClick={() => removeRow(row.row_id)} aria-label={`Remove row ${row.source_row}`} data-testid={`remove-identifier-row-${index}-button`}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex flex-col gap-3 border-t border-zinc-200 bg-white px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-xs text-zinc-500">
                  {dirty && <span className="flex items-center text-amber-700"><AlertCircle className="mr-1 h-4 w-4" />Revalidate edited rows before importing.</span>}
                  {!dirty && errorRows.length > 0 && `${errorRows.length} blocked row${errorRows.length === 1 ? '' : 's'} will remain for correction.`}
                  {!dirty && errorRows.length === 0 && <span className="flex items-center text-emerald-700"><CheckCircle2 className="mr-1 h-4 w-4" />All rows are ready.</span>}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={importing} data-testid="cancel-bulk-identifier-import-button">Cancel</Button>
                  <Button variant="outline" onClick={() => revalidate()} disabled={!dirty || loading || importing} data-testid="revalidate-identifier-rows-button"><RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />Revalidate corrections</Button>
                  <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setConfirmOpen(true)} disabled={dirty || !validRows.length || loading || importing} data-testid="review-identifier-import-button"><Upload className="mr-2 h-4 w-4" />Import {validRows.length} valid row{validRows.length === 1 ? '' : 's'}</Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="confirm-bulk-identifier-import-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm identifier import?</AlertDialogTitle>
            <AlertDialogDescription>{validRows.length} valid row{validRows.length === 1 ? '' : 's'} will be saved. {errorRows.length ? `${errorRows.length} blocked row${errorRows.length === 1 ? '' : 's'} will not be saved and will remain available for correction.` : 'All preview rows are valid.'}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirmOpen(false)} disabled={importing} data-testid="back-to-identifier-preview-button">Back to preview</AlertDialogCancel>
            <AlertDialogAction onClick={confirmImport} disabled={importing} className="bg-blue-600 text-white hover:bg-blue-700" data-testid="confirm-bulk-identifier-import-button">{importing ? <><LoaderCircle className="mr-2 h-4 w-4 animate-spin" />Importing…</> : `Confirm ${validRows.length} rows`}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ConfirmDialog
        open={discardOpen}
        onOpenChange={setDiscardOpen}
        title={discardPrompt?.title}
        description={discardPrompt?.description}
        confirmLabel={discardPrompt?.action === 'replace' ? 'Discard and replace file' : 'Discard and close'}
        cancelLabel="Keep editing"
        destructive
        onConfirm={confirmDiscard}
        testId="discard-identifier-edits-confirm"
      />
    </>
  );
};